import React, { memo, useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Button } from 'primereact/button'

import { Dialog } from '../../../../../common/uikit/Dialog'
import { Dropdown } from '../../../../../common/uikit/Dropdown'
import useText from '../../../../../common/hooks/useText'
import {
    AVAILABLE_LANGUAGES,
    AVAILABLE_LANGUAGES_LOCAL,
    useLocalizationContext,
} from '../../../../../common/components/localization-provider'
import { getNamesWithFallback } from '../../../../../i18n/countries'
import { DEFAULT_BILLING_COUNTRY } from '../../../../../common/utils/company-settings'

export type CompleteOrganisationInformationValues = {
    billingCountry: string
    communicationLanguage: string
}

export type CompleteOrganisationInformationDialogProps = {
    visible: boolean
    initialValues: Partial<CompleteOrganisationInformationValues>
    isSaving: boolean
    onHide: () => void
    onSubmit: (values: CompleteOrganisationInformationValues) => void | Promise<void>
}

const formId = 'complete-organisation-information-form'
const communicationLanguageOptions = AVAILABLE_LANGUAGES.map((value) => ({
    label: AVAILABLE_LANGUAGES_LOCAL[value] ?? value,
    value,
}))

export const CompleteOrganisationInformationDialog = memo(function CompleteOrganisationInformationDialog({
    visible,
    initialValues,
    isSaving,
    onHide,
    onSubmit,
}: CompleteOrganisationInformationDialogProps): React.ReactElement {
    const text = useText()
    const { locale } = useLocalizationContext()
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isSubmitted, isSubmitting },
    } = useForm<CompleteOrganisationInformationValues>({
        defaultValues: {
            billingCountry: DEFAULT_BILLING_COUNTRY,
            communicationLanguage: 'en',
        },
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    })

    const countryOptions = useMemo(
        () =>
            Object.entries(getNamesWithFallback(locale))
                .map(([value, label]) => ({ label, value }))
                .sort((a, b) => a.label.localeCompare(b.label, locale)),
        [locale]
    )
    useEffect(() => {
        if (!visible) return

        reset({
            billingCountry: initialValues.billingCountry?.trim() || DEFAULT_BILLING_COUNTRY,
            communicationLanguage: initialValues.communicationLanguage?.trim() || 'en',
        })
    }, [initialValues.billingCountry, initialValues.communicationLanguage, reset, visible])

    const save = (values: CompleteOrganisationInformationValues) => onSubmit(values)
    const isSaveDisabled = isSaving || isSubmitting || (isSubmitted && Object.keys(errors).length > 0)
    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button type="button" label={text('btnCancel')} text onClick={onHide} />
            <Button
                type="submit"
                form={formId}
                label={text('btnSave')}
                loading={isSaving || isSubmitting}
                disabled={isSaveDisabled}
            />
        </div>
    )

    return (
        <Dialog
            visible={visible}
            header={text('lblCompleteOrganisationInformationDialogTitle')}
            onHide={onHide}
            footer={footer}
        >
            <form id={formId} noValidate onSubmit={handleSubmit(save)}>
                <p className="m-0 mb-4">{text('msgCompleteOrganisationInformationDialogDescription')}</p>
                <div className="flex flex-column gap-3">
                    <Controller
                        name="billingCountry"
                        control={control}
                        rules={{ required: text('msgBillingCountryRequired') }}
                        render={({ field }) => (
                            <Dropdown
                                id="billing-country"
                                label={text('lblBillingCountry')}
                                options={countryOptions}
                                required
                                value={field.value || null}
                                showClear={false}
                                onChange={(event) => field.onChange(event.value)}
                                onBlur={field.onBlur}
                                error={errors.billingCountry?.message}
                            />
                        )}
                    />
                    <Controller
                        name="communicationLanguage"
                        control={control}
                        rules={{ required: text('msgBillingCommunicationLanguageRequired') }}
                        render={({ field }) => (
                            <Dropdown
                                id="billing-organisation-communication-language"
                                label={text('lblCommunicationLanguage')}
                                options={communicationLanguageOptions}
                                required
                                value={field.value}
                                showClear={false}
                                onChange={(event) => field.onChange(event.value)}
                                onBlur={field.onBlur}
                                error={errors.communicationLanguage?.message}
                            />
                        )}
                    />
                </div>
            </form>
        </Dialog>
    )
})
